import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, tasksAPIExtended } from '../services/api';
import { useToast } from './Toast';
import { formatCurrency } from '../utils/formatters';
import {
  Users, ClipboardList, Home, DollarSign,
  CheckCircle2, Phone, MessageCircle,
  ArrowUpRight
} from 'lucide-react';

function formatPKR(amount) {
  const num = Number(amount) || 0;
  if (num >= 10000000) return `PKR ${(num / 10000000).toFixed(1).replace(/\.0$/, '')} Cr`;
  if (num >= 100000) return `PKR ${(num / 100000).toFixed(1).replace(/\.0$/, '')} Lac`;
  if (num >= 1000) return `PKR ${(num / 1000).toFixed(1).replace(/\.0$/, '')} K`;
  return formatCurrency(num);
}

const statusBadgeStyle = {
  new: { bg: '#E6F1FB', color: '#185FA5' },
  contacted: { bg: '#E1F5EE', color: '#0F6E56' },
  viewing: { bg: '#FAEEDA', color: '#854F0B' },
  negotiating: { bg: '#EEEDFE', color: '#534AB7' },
  won: { bg: '#EAF3DE', color: '#3B6D11' },
  closed: { bg: '#EAF3DE', color: '#3B6D11' },
  lost: { bg: '#FCEBEB', color: '#A32D2D' },
  follow_up: { bg: '#E6F1FB', color: '#185FA5' },
  in_progress: { bg: '#EEEDFE', color: '#534AB7' },
  archived: { bg: '#F1F0EC', color: '#888780' },
};

const propertyBadgeStyle = {
  available: { bg: '#EAF3DE', color: '#3B6D11' },
  sold: { bg: '#FCEBEB', color: '#A32D2D' },
  reserved: { bg: '#FAEEDA', color: '#854F0B' },
  inactive: { bg: '#F1F0EC', color: '#888780' },
};

const avatarColors = ['#185FA5', '#0F6E56', '#854F0B', '#534AB7', '#A32D2D'];

function getBadgeStyle(status, map) {
  const s = (status || '').toLowerCase();
  return map[s] || { bg: '#F1F0EC', color: '#888780' };
}

const pipeColors = { new: '#185FA5', contacted: '#0F6E56', viewing: '#854F0B', negotiating: '#534AB7', won: '#3B6D11', lost: '#A32D2D', follow_up: '#378ADD', in_progress: '#534AB7', closed: '#3B6D11', archived: '#888780' };
const sourceColors = ['#185FA5', '#0F6E56', '#854F0B', '#534AB7', '#A32D2D', '#639922', '#EF9F27', '#378ADD', '#1D9E75', '#888780', '#E06C6C', '#6B7280'];

function getCommTypeStyle(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('call')) return { bg: '#E6F1FB', color: '#185FA5', border: '3px solid #185FA5', Icon: Phone, dot: '#185FA5' };
  if (t.includes('meeting')) return { bg: '#EEEDFE', color: '#534AB7', border: '3px solid #534AB7', Icon: Users, dot: '#534AB7' };
  return { bg: '#FAEEDA', color: '#854F0B', border: '3px solid #854F0B', Icon: MessageCircle, dot: '#639922' };
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [kpi, setKpi] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);
  const [dealsClosingSoon, setDealsClosingSoon] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
    loadAll();
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const results = await Promise.allSettled([
        analyticsAPI.getDashboardMain(),
        analyticsAPI.getRecentLeads(),
        u.role === 'admin' ? analyticsAPI.getAgentLeaderboard() : Promise.resolve({ data: [] }),
        analyticsAPI.getOverdueCount(),
        analyticsAPI.getDealsClosingSoon(),
        analyticsAPI.getSources(),
        analyticsAPI.getUpcomingTasks(),
      ]);
      const d = (r) => (r.status === 'fulfilled' ? r.value.data : null);
      const dArr = (r) => (r.status === 'fulfilled' && Array.isArray(r.value.data) ? r.value.data : []);
      const main = d(results[0]) || {};
      setKpi({
        totalLeads: main.totalLeads || 0,
        leadsVariance: main.leadsVariance || 0,
        tasksDueToday: main.tasksDueToday || 0,
        activeProperties: main.activeProperties || 0,
        revenueThisMonth: main.revenueThisMonth || 0,
        revenuePrevMonth: main.revenuePrevMonth || 0,
        revenueVariance: main.revenueVariance || 0,
      });
      setTodayTasks(main.todayTasks || []);
      setPipeline(main.pipeline || []);
      setRecentLeads(dArr(results[1]));
      setLeaderboard(dArr(results[2]));
      const overdueRes = results[3];
      setOverdueCount(overdueRes.status === 'fulfilled' ? (overdueRes.value.data?.count || 0) : 0);
      setDealsClosingSoon(dArr(results[4]));
      setLeadSources(dArr(results[5]));
      setUpcomingTasks(dArr(results[6]));
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkDone = async (taskId) => {
    try {
      setCompletingTaskId(taskId);
      await tasksAPIExtended.markAsDone(taskId);
      showToast('Task marked as done', 'success');
      setTodayTasks((prev) => prev.filter((t) => t.id !== taskId));
      setKpi((prev) => prev ? { ...prev, tasksDueToday: Math.max(0, prev.tasksDueToday - 1) } : prev);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to complete task', 'error');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const now = new Date();
  const formatTime = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  const formatDate = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const timeAgo = (dt) => {
    if (!dt) return '';
    const sec = Math.floor((Date.now() - new Date(dt)) / 1000);
    if (sec < 60) return 'just now';
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return formatDate(dt);
  };

  const todayStr = now.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const firstName = user?.full_name?.split(' ')[0] || user?.username || 'there';
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const totalPipelineLeads = pipeline.reduce((s, p) => s + Number(p.count), 0);
  const totalDeals = leaderboard.reduce((s, a) => s + (a.deals_closed || 0), 0);
  const totalTasks = kpi?.tasksDueToday || 0;
  const hasOverdueTasks = overdueCount > 0;

  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto' }}>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} } .pulse { animation: pulse 1.5s ease-in-out infinite; }`}</style>
        <div className="pulse" style={{ width: '260px', height: '22px', background: '#F1F0EC', borderRadius: '4px', marginBottom: '8px' }} />
        <div className="pulse" style={{ width: '200px', height: '13px', background: '#F1F0EC', borderRadius: '4px', marginBottom: '24px' }} />
      <div className="dboard-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
          {[1,2,3,4].map(i => <div key={i} className="pulse" style={{ height: '96px', background: '#F1F0EC', borderRadius: '8px' }} />)}
        </div>
        <div className="dboard-main-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
          <div className="pulse" style={{ height: '280px', background: '#F1F0EC', borderRadius: '12px' }} />
          <div className="pulse" style={{ height: '280px', background: '#F1F0EC', borderRadius: '12px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="dboard-wrap" style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto' }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        .d-card { background: #FFFFFF; border: 1px solid rgba(0,0,0,0.08); border-radius: 12px; overflow: hidden; }
        .d-title { font-size: 14px; font-weight: 500; color: #1A1A1A; }
        .d-divider { height: 0.5px; background: rgba(0,0,0,0.08); margin: 0; }
        .d-row { padding: 8px 24px; display: flex; align-items: center; gap: 12px; cursor: pointer; transition: background 0.15s; }
        .d-row:hover { background: #F8F8F6; }
        @media (max-width: 1023px) { .dboard-wrap { padding: 16px !important; } .dboard-wrap .d-card-header { padding: 14px 16px !important; } .dboard-wrap .d-divider + div { padding-left: 16px !important; padding-right: 16px !important; } .dboard-wrap .d-row { padding: 8px 16px !important; } }
        @media (max-width: 767px) { .dboard-kpi { grid-template-columns: repeat(2, 1fr) !important; } .dboard-main-grid { grid-template-columns: 1fr !important; } .dboard-bottom-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 480px) { .dboard-wrap { padding: 12px !important; } .dboard-kpi { grid-template-columns: 1fr !important; } .dboard-kpi-val { font-size: 22px !important; } .dboard-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; } .dboard-header-badges { flex-wrap: wrap !important; } .dboard-deals-scroll > div { min-width: 150px !important; } }
      `}</style>

      {/* Header */}
      <div className="dboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 500, color: '#1A1A1A', margin: 0 }}>{greeting}, {firstName}</h1>
          <p style={{ fontSize: '13px', color: '#888780', marginTop: '2px' }}>{todayStr}</p>
        </div>
        <div className="dboard-header-badges" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px', background: '#E6F1FB', color: '#185FA5', whiteSpace: 'nowrap' }}>
            {kpi?.totalLeads || 0} leads
          </span>
          <span style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px', background: hasOverdueTasks ? '#FCEBEB' : '#FAEEDA', color: hasOverdueTasks ? '#A32D2D' : '#854F0B', whiteSpace: 'nowrap' }}>
            {totalTasks} tasks
          </span>
          <span style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px', background: '#EAF3DE', color: '#3B6D11', whiteSpace: 'nowrap' }}>
            {totalDeals} deals
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dboard-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        {(() => {
          const cards = [
            { label: 'Total Leads', value: kpi?.totalLeads, variance: kpi?.leadsVariance, prevValue: null, icon: Users, borderColor: '#378ADD', hasPrev: true },
            { label: 'Tasks Due Today', value: kpi?.tasksDueToday, variance: kpi?.tasksVariance, prevValue: null, icon: ClipboardList, borderColor: hasOverdueTasks ? '#A32D2D' : '#EF9F27' },
            { label: 'Active Properties', value: kpi?.activeProperties, variance: kpi?.propertiesVariance, prevValue: null, icon: Home, borderColor: '#1D9E75' },
            { label: 'Revenue This Month', value: kpi?.revenueThisMonth, variance: kpi?.revenueVariance, prevValue: kpi?.revenuePrevMonth, icon: DollarSign, borderColor: '#639922', isCurrency: true, hasPrev: true },
          ];
          return cards.map((c) => {
            const v = c.variance;
            const isPos = v > 0;
            const isNeg = v < 0;
            const hasVariance = v !== 0 && v != null && v !== undefined && !Number.isNaN(v);
            const showPrevAboveZero = c.hasPrev && c.prevValue != null && c.prevValue > 0;
            let varText;
            if (hasVariance) {
              varText = `${isPos ? '↑' : '↓'} ${Math.abs(v)}%`;
            } else if (c.hasPrev && !showPrevAboveZero) {
              varText = 'No previous data';
            } else if (c.hasPrev && showPrevAboveZero) {
              varText = 'No change';
            } else {
              varText = '';
            }
            return (
              <div key={c.label} style={{ background: '#F1F0EC', borderRadius: '8px', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '2px', borderLeft: `4px solid ${c.borderColor}`, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <c.icon size={16} color="#888780" />
                  <span style={{ fontSize: '12px', fontWeight: 400, color: '#888780' }}>{c.label}</span>
                </div>
                <div className="dboard-kpi-val" style={{ fontSize: '28px', fontWeight: 500, color: '#1A1A1A', lineHeight: 1.15 }}>
                  {c.isCurrency ? formatPKR(c.value) : c.isPercent ? (c.value != null ? `${c.value}%` : '—') : (c.value ?? 0)}
                </div>
                <div style={{ fontSize: '11px', color: isPos ? '#3B6D11' : isNeg ? '#A32D2D' : '#B4B2A9' }}>
                  {varText || (c.hasPrev ? <span style={{ color: '#B4B2A9' }}>No previous data</span> : '')}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Deals Closing Soon */}
      <div className="d-card" style={{ marginBottom: '12px' }}>
        <div className="d-card-header" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="d-title">Deals Closing Soon</span>
          {dealsClosingSoon.length > 0 && (
            <span style={{ fontSize: '12px', color: '#B4B2A9' }}>{dealsClosingSoon.length} {dealsClosingSoon.length === 1 ? 'deal' : 'deals'}</span>
          )}
        </div>
        <div className="d-divider" />
        {dealsClosingSoon.length === 0 ? (
          <div style={{ padding: '8px 24px', height: '40px', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#888780' }}>No deals closing soon</span>
          </div>
        ) : (
          <div className="dboard-deals-scroll" style={{ display: 'flex', overflowX: 'auto', gap: '0' }}>
            {dealsClosingSoon.map((deal, idx) => {
              const tStyle = getCommTypeStyle(deal.follow_up_type);
              const initial = (deal.name || '?').charAt(0).toUpperCase();
              const ai = deal.name ? deal.name.length % avatarColors.length : 0;
              return (
                <div key={deal.id} style={{ flex: '1', minWidth: '180px', borderRight: idx < dealsClosingSoon.length - 1 ? '0.5px solid rgba(0,0,0,0.08)' : 'none', cursor: 'pointer' }}
                     onClick={() => navigate(`/leads/${deal.id}`)}
                     onMouseEnter={e => e.currentTarget.style.background = '#F8F8F6'}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: avatarColors[ai], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#fff' }}>{initial}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{deal.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                        <span style={{ fontSize: '11px', color: '#888780' }}>{deal.due_date ? formatDate(deal.due_date) : ''}</span>
                        {deal.follow_up_type && (
                          <span style={{ fontSize: '10px', fontWeight: 500, padding: '1px 6px', borderRadius: '10px', background: tStyle.bg, color: tStyle.color }}>
                            {deal.follow_up_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main 2-col grid */}
      <div className="dboard-main-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {overdueCount > 0 && (
            <div
              onClick={() => navigate('/tasks')}
              style={{ background: '#FCEBEB', borderRadius: '8px', padding: '8px 20px', marginBottom: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(163,45,45,0.15)' }}
            >
              <span style={{ fontSize: '13px', color: '#A32D2D', fontWeight: 500 }}>
                {overdueCount} overdue follow-up{overdueCount > 1 ? 's' : ''} from previous days
              </span>
              <ArrowUpRight size={14} color="#A32D2D" style={{ flexShrink: 0 }} />
            </div>
          )}

          {/* Today's Follow-ups */}
          <div className="d-card">
          <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="d-title">Today's Follow-ups</span>
            <span style={{ fontSize: '12px', color: '#B4B2A9' }}>{todayTasks.length} {todayTasks.length === 1 ? 'task' : 'tasks'}</span>
          </div>
          <div className="d-divider" />
          {todayTasks.length === 0 ? (
            <div style={{ padding: '8px 24px', display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}>
              <CheckCircle2 size={16} color="#3B6D11" />
              <span style={{ fontSize: '13px', color: '#888780' }}>All caught up — no tasks due today</span>
            </div>
          ) : (
            todayTasks.map((task, idx) => {
              const isOverdue = task.due_date && new Date(task.due_date) < now && task.status !== 'completed';
              const tStyle = getCommTypeStyle(task.type);
              return (
                <div key={task.id}>
                  <div className="d-row" style={{ borderLeft: `3px solid ${isOverdue ? '#A32D2D' : tStyle.color}`, paddingLeft: '21px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: tStyle.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <tStyle.Icon size={14} color={tStyle.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {task.lead_name || 'Unknown Lead'}
                        </span>
                        {isOverdue && (
                          <span style={{ fontSize: '10px', fontWeight: 500, padding: '2px 8px', borderRadius: '20px', background: '#FCEBEB', color: '#A32D2D', whiteSpace: 'nowrap' }}>Overdue</span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888780', marginTop: '1px' }}>
                        {task.title}{task.due_date ? ` • ${formatTime(task.due_date)}` : ''}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleMarkDone(task.id); }}
                      disabled={completingTaskId === task.id}
                      style={{ padding: '3px 14px', fontSize: '11px', fontWeight: 500, borderRadius: '20px', border: 'none', background: '#EAF3DE', color: '#3B6D11', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {completingTaskId === task.id ? '...' : 'Mark Done'}
                    </button>
                  </div>
                  {idx < todayTasks.length - 1 && <div className="d-divider" />}
                </div>
              );
            })
          )}
        </div>

        {upcomingTasks.length > 0 && (
          <>
            <div className="d-divider" style={{ margin: '8px 0' }} />
            <div style={{ padding: '0 24px 12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 500, color: '#888780', marginBottom: '8px' }}>Upcoming (next 7 days)</div>
              {upcomingTasks.map((task) => {
                const tStyle = getCommTypeStyle(task.type);
                return (
                  <div key={task.id} className="d-row" style={{ padding: '6px 8px', borderRadius: '6px', marginBottom: '2px', cursor: 'default' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: tStyle.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {task.lead_name || 'Unknown'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#B4B2A9', whiteSpace: 'nowrap' }}>{task.due_date ? formatDate(task.due_date) : ''}</span>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 500, padding: '1px 6px', borderRadius: '10px', background: tStyle.bg, color: tStyle.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {task.type || 'Follow-up'}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        </div>

        {/* Agent Leaderboard */}
        <div className="d-card">
          <div className="d-card-header" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="d-title">Agent Leaderboard</span>
          </div>
          <div className="d-divider" />
          {user?.role !== 'admin' ? (
            <div style={{ padding: '8px 24px', height: '40px', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#888780' }}>Available for admins only.</span>
            </div>
          ) : leaderboard.length === 0 ? (
            <div style={{ padding: '8px 24px', height: '40px', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#888780' }}>No activity logged this month</span>
            </div>
          ) : (
            <div>
              <div style={{ padding: '6px 24px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#B4B2A9', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
                <span style={{ width: '28px', flexShrink: 0 }}>#</span>
                <span style={{ flex: 1 }}>Agent</span>
                <span style={{ width: '40px', textAlign: 'center' }}>Leads</span>
                <span style={{ width: '40px', textAlign: 'center' }}>Deals</span>
              </div>
              {leaderboard.map((agent, idx) => (
                <div key={agent.id} style={{ borderLeft: idx === 0 ? '3px solid #3B6D11' : '3px solid transparent' }}>
                  <div style={{ padding: '8px 24px', paddingLeft: idx === 0 ? '21px' : '24px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.15s' }}
                    className="d-row-hover"
                    onMouseEnter={e => e.currentTarget.style.background = '#F8F8F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ width: '28px', flexShrink: 0, fontSize: '12px', fontWeight: 500, color: idx < 3 ? '#1A1A1A' : '#B4B2A9', textAlign: 'center' }}>{idx + 1}</span>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: avatarColors[idx % 5], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '10px', fontWeight: 500, color: '#fff' }}>{(agent.full_name || agent.username || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agent.full_name || agent.username}</span>
                    </div>
                    <span style={{ width: '40px', textAlign: 'center', fontSize: '13px', color: '#1A1A1A' }}>{agent.leads_handled || 0}</span>
                    <span style={{ width: '40px', textAlign: 'center', fontSize: '13px', fontWeight: 500, color: '#3B6D11' }}>{agent.deals_closed || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pipeline + Recent Leads */}
      <div className="dboard-bottom-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        {/* Pipeline */}
        <div className="d-card">
          <div className="d-card-header" style={{ padding: '20px 24px' }}>
            <span className="d-title">Pipeline Status</span>
            {totalPipelineLeads > 0 && (
              <span style={{ fontSize: '12px', color: '#888780', marginLeft: '8px', fontWeight: 400 }}>{totalPipelineLeads} total across {pipeline.length} stages</span>
            )}
          </div>
          <div className="d-divider" />
          <div style={{ padding: '12px 24px 20px' }}>
            {pipeline.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#888780', padding: '8px 0' }}>No pipeline data</div>
            ) : (
              pipeline.map((stage) => {
                const pct = totalPipelineLeads > 0 ? (stage.count / totalPipelineLeads) * 100 : 0;
                const s = (stage.status || '').toLowerCase();
                return (
                  <div key={stage.status} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#888780', textTransform: 'capitalize', minWidth: '80px' }}>
                        {s === 'in_progress' ? 'In Progress' : s.replace(/_/g, ' ')}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#1A1A1A' }}>{stage.count}</span>
                      <span style={{ fontSize: '11px', color: '#B4B2A9' }}>· {Math.round(pct)}%</span>
                    </div>
                    <div style={{ height: '10px', borderRadius: '5px', background: '#F1F0EC', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, minWidth: stage.count > 0 ? '24px' : '0', borderRadius: '5px', background: pipeColors[s] || '#185FA5', transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {leadSources.length > 0 && (
            <>
              <div className="d-divider" />
              <div style={{ padding: '12px 24px 20px' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A', marginBottom: '12px' }}>Lead Sources</div>
                {(() => {
                  const total = leadSources.reduce((s, x) => s + x.count, 0);
                  const maxCount = Math.max(...leadSources.map(x => x.count), 1);
                  return leadSources.map((src, idx) => {
                    const pct = total > 0 ? (src.count / total) * 100 : 0;
                    const barPct = (src.count / maxCount) * 100;
                    return (
                      <div key={src.source} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: '#888780', minWidth: '80px' }}>{src.source}</span>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#1A1A1A' }}>{src.count}</span>
                          <span style={{ fontSize: '11px', color: '#B4B2A9' }}>· {Math.round(pct)}%</span>
                        </div>
                        <div style={{ height: '10px', borderRadius: '5px', background: '#F1F0EC', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.max(barPct, 4)}%`, minWidth: src.count > 0 ? '24px' : '0', borderRadius: '5px', background: sourceColors[idx % sourceColors.length], transition: 'width 0.5s ease' }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </div>

        {/* Recent Leads */}
        <div className="d-card">
          <div className="d-card-header" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="d-title">Recent Leads</span>
            <button onClick={() => navigate('/leads')} style={{ fontSize: '12px', color: '#185FA5', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500 }}>View All</button>
          </div>
          <div className="d-divider" />
          {recentLeads.length === 0 ? (
            <div style={{ padding: '8px 24px', height: '40px', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#888780' }}>No leads yet</span>
            </div>
          ) : (
            recentLeads.map((lead, idx) => {
              const initial = (lead.name || '?').charAt(0).toUpperCase();
              const ai = lead.name ? lead.name.length % avatarColors.length : 0;
              return (
                <div key={lead.id}>
                  <div className="d-row" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: avatarColors[ai], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#fff' }}>{initial}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.name}</div>
                      <div style={{ fontSize: '12px', color: '#888780' }}>{lead.source || ''}{lead.phone ? ` · ${lead.phone}` : ''}</div>
                    </div>
                    {(() => { const bs = getBadgeStyle(lead.status, statusBadgeStyle); return (
                      <span style={{ fontSize: '10px', fontWeight: 500, padding: '2px 8px', borderRadius: '20px', background: bs.bg, color: bs.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {lead.status ? lead.status.replace(/_/g, ' ') : 'Unknown'}
                      </span>
                    ); })()}
                  </div>
                  {idx < recentLeads.length - 1 && <div className="d-divider" />}
                </div>
              );
            })
          )}
        </div>

        </div>
      </div>
  );
};

export default Dashboard;
